/* The native product form and each existing product engine retain commerce ownership. */
(() => {
  const mobile = matchMedia('(max-width: 749px)');
  const detailOwners = new Set();
  let detailId = 0;
  const modalOwners = new Set();
  let scrollSnapshot;
  function lockScroll(dialog) {
    if (!modalOwners.size) {
      const body = document.body;
      const html = document.documentElement;
      scrollSnapshot = { y:window.scrollY, properties:[] };
      for (const [element,name,value] of [[html,'overflow','hidden'],[body,'position','fixed'],[body,'top',`-${window.scrollY}px`],[body,'left','0px'],[body,'right','0px']]) {
        scrollSnapshot.properties.push({ element,name,value, before:element.style.getPropertyValue(name), priority:element.style.getPropertyPriority(name) });
        element.style.setProperty(name,value);
      }
    }
    modalOwners.add(dialog);
  }
  function unlockScroll(dialog) {
    modalOwners.delete(dialog);
    if (modalOwners.size || !scrollSnapshot) return;
    const snapshot = scrollSnapshot; scrollSnapshot = undefined;
    for (const { element,name,value,before,priority } of snapshot.properties) {
      // Preserve a new owner's later style mutation instead of removing it.
      if (element.style.getPropertyValue(name) === value) {
        if (before) element.style.setProperty(name,before,priority);
        else element.style.removeProperty(name);
      }
    }
    window.scrollTo({ top:snapshot.y, behavior:'instant' });
  }
  function initPicker(root) {
    if (root.dataset.b2hReady) return;
    const select = root.querySelector('[data-pdp-native-variant]');
    const disclosure = root.querySelector('[data-pdp-picker-disclosure]');
    const form = root.closest('form');
    if (!select || !form) return;
    const studio = root.dataset.adapter === 'studio';
    const studioOptions = form.querySelector('[data-product-options]');
    // Studio announces readiness after binding all original option/validation handlers.
    const studioRoot = form.closest('[data-b2h-product-studio]');
    if (studio && studioRoot?.dataset.initialized !== 'true') {
      if (!root.dataset.waitingForStudio) {
        root.dataset.waitingForStudio = 'true';
        studioRoot?.addEventListener('b2h:studio-ready', () => initPicker(root), { once:true });
      }
      return;
    }
    root.dataset.b2hReady = 'true';
    const choices = [...root.querySelectorAll('[data-pdp-picker-choice]')];
    let pointerChoice;
    // WebKit forwards a tapped label to its radio with click.detail === 0.
    // Track the pointer source so touch closes the list while arrow keys keep it open.
    root.addEventListener('pointerdown', event => {
      pointerChoice = event.target.closest('.b2h-pdp-picker__row')?.querySelector('[data-pdp-picker-choice]');
    });
    root.addEventListener('keydown', () => { pointerChoice = undefined; });
    function sync() {
      const selected = select.selectedOptions[0];
      if (!selected) return;
      const label = root.querySelector('[data-pdp-picker-selected]');
      if (label) label.textContent = selected.textContent.trim();
      for (const choice of choices) choice.checked = choice.value === select.value;
    }
    function apply() {
      if (studio && studioOptions) {
        const variants = JSON.parse(form.querySelector('[data-product-variants]').textContent);
        const variant = variants.find(item => String(item.id) === select.value);
        if (!variant) return;
        let changed;
        for (const [index,value] of variant.options.entries()) {
          const field = studioOptions.querySelector(`[data-option-index="${index}"]`);
          const input = [...(field?.querySelectorAll('input') || [])].find(item => item.value === value);
          if (input) { input.checked = true; changed = input; }
        }
        changed?.dispatchEvent(new Event('change', { bubbles:true }));
      } else if (root.dataset.adapter === 'native') {
        const selected = select.selectedOptions[0];
        const section = root.closest('section');
        const price = section?.querySelector('[data-pdp-price]');
        if (price) { price.textContent = selected.dataset.price; price.removeAttribute('aria-label'); }
        const button = form.querySelector('button[type="submit"]');
        if (button) button.disabled = selected.dataset.eligible !== 'true';
        const url = new URL(location.href);
        url.searchParams.set('variant',select.value);
        history.replaceState({},'',url);
        if (selected.dataset.mediaId) section?.querySelector('media-gallery')?.goToMediaId?.(selected.dataset.mediaId);
        const gallery = section?.querySelector('slide-section');
        const swiper = gallery?.globalSlide || gallery?.swiper;
        const index = [...(swiper?.slides || [])].findIndex(slide => slide.dataset.mediaId === selected.dataset.mediaId);
        if (selected.dataset.mediaId && index >= 0) swiper.slideTo(index);
      }
      sync();
    }
    select.addEventListener('change', apply);
    for (const choice of choices) choice.addEventListener('change', () => {
      if (!choice.checked || choice.disabled) return;
      select.value = choice.value;
      select.dispatchEvent(new Event('change', { bubbles:true }));
    });
    for (const choice of choices) choice.addEventListener('click', event => {
      const fromPointer = event.detail > 0 || pointerChoice === choice;
      pointerChoice = undefined;
      if (!disclosure || !fromPointer || choice.disabled) return;
      disclosure.open = false;
      disclosure.querySelector('summary').focus({ preventScroll:true });
    });
    // A singleton is a fixed specification; retire the duplicate Studio radio too.
    if (studioOptions) studioOptions.hidden = true;
    if (disclosure) {
      select.hidden = true;
      disclosure.hidden = false;
      disclosure.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !disclosure.open) return;
        event.preventDefault(); disclosure.open = false;
        disclosure.querySelector('summary').focus({ preventScroll:true });
      });

    }
    (studioRoot || form.closest('section'))?.addEventListener('b2h:variant-change', sync);
    // Resolve deep-linked media/price through the same original change path once.
    select.dispatchEvent(new Event('change', { bubbles:true }));
  }
  function initSize(root) {
    if (root.dataset.b2hReady || !('showModal' in HTMLDialogElement.prototype)) return;
    const fallback = root.querySelector('[data-size-fallback]');
    const trigger = root.querySelector('[data-size-trigger]');
    const content = root.querySelector('[data-size-content]');
    const dialog = root.querySelector('dialog');
    if (!fallback || !trigger || !dialog || !content) return;
    root.dataset.b2hReady = 'true';
    // Move one original content instance; no duplicate IDs or hidden tab stops.
    dialog.append(content); fallback.hidden = true; trigger.hidden = false;
    trigger.addEventListener('click', () => {
      lockScroll(dialog);
      try { dialog.showModal(); } catch (error) { unlockScroll(dialog); throw error; }
    });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const targets = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length && !element.closest('[hidden],[inert]'));
      const first = targets[0], last = targets[targets.length-1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    dialog.querySelector('[data-size-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) {
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    }});
    dialog.addEventListener('close', () => { unlockScroll(dialog); trigger.focus({ preventScroll:true }); });
  }
  function initDetails(root) {
    if (root.dataset.b2hReady || root.textContent.trim().length < 420) return;
    const nodes = [...root.childNodes];
    let boundary = -1;
    for (const [index,node] of nodes.entries()) {
      if (node.nodeType === Node.ELEMENT_NODE && !/^H[1-6]$/.test(node.tagName) && node.textContent.trim().length > 45) { boundary=index; break; }
    }
    if (boundary >= 0) {
      let next = boundary + 1;
      while (next < nodes.length && nodes[next].nodeType === Node.TEXT_NODE && !nodes[next].textContent.trim()) next++;
      if (nodes[next]?.nodeType === Node.ELEMENT_NODE && /^(UL|OL)$/.test(nodes[next].tagName)) boundary = next;
    }
    if (boundary < 0 || !nodes.slice(boundary+1).some(node => node.textContent.trim())) return;
    root.dataset.b2hReady = 'true';
    const remainder = document.createElement('div');
    remainder.className = 'b2h-pdp-details__remainder';
    remainder.id = `B2hDetailsRemainder-${++detailId}`;
    remainder.dataset.pdpDetailsRemainder = '';
    for (const node of nodes.slice(boundary+1)) remainder.append(node);
    root.append(remainder);
    const toggle = document.createElement('button');
    toggle.type = 'button'; toggle.className = 'b2h-pdp-details__toggle';
    toggle.setAttribute('aria-controls',remainder.id);
    toggle.dataset.pdpDetailsToggle = '';
    root.append(toggle);
    let expanded = false;
    function refresh() {
      const collapsed = mobile.matches && !expanded;
      toggle.hidden = !mobile.matches;
      if (collapsed && remainder.contains(document.activeElement)) toggle.focus({ preventScroll:true });
      remainder.hidden = collapsed;
      toggle.setAttribute('aria-expanded',String(!collapsed));
      toggle.textContent = collapsed ? root.dataset.moreLabel : root.dataset.lessLabel;
    }
    toggle.addEventListener('click', () => { expanded = !expanded; refresh(); });
    const owner = { root, refresh };
    detailOwners.add(owner); refresh();
  }
  const entryOwners = new Map();
  function initEntry(link) {
    if (entryOwners.has(link)) return;
    const target = document.getElementById(link.dataset.entryTarget);
    const form = target?.matches('form') ? target : target?.querySelector('form[action*="/cart/add"]');
    const primary = form?.querySelector('button[type="submit"]');
    if (!form || !primary) return;
    const controller = new AbortController();
    const options = { signal:controller.signal };
    const missingInput = () => [...form.elements].find(field => field.willValidate && !field.disabled && !field.validity.valid);
    const refresh = () => {
      const missing = missingInput();
      link.dataset.entryState = primary.disabled ? 'unavailable' : missing ? 'incomplete' : 'review';
      const label = link.querySelector('[data-entry-label]');
      if (label) label.textContent = link.hasAttribute('data-entry-customize')
        ? missing?.type === 'file' ? link.dataset.entryPhoto : missing ? link.dataset.entryRequired : link.dataset.entryReview
        : link.dataset.entryDefault;
    };
    link.addEventListener('click', event => {
      const missing = missingInput();
      const destination = missing && missing.getClientRects().length ? missing : target;
      if (!destination) return;
      event.preventDefault();
      destination.scrollIntoView({ block:'start', behavior:'instant' });
      if (destination === missing) destination.focus({ preventScroll:true });
      else primary.focus({ preventScroll:true });
    },options);
    form.addEventListener('input',refresh,options);
    form.addEventListener('change',refresh,options);
    const availability = new MutationObserver(refresh);
    availability.observe(primary,{ attributes:true, attributeFilter:['disabled'] });
    const visibility = new IntersectionObserver(entries => {
      link.classList.toggle('is-purchase-visible',entries[0].isIntersecting);
    },{ threshold:.5 });
    visibility.observe(primary);
    entryOwners.set(link,{ controller,availability,visibility });
    refresh();
  }
  function discover() {
    document.querySelectorAll('[data-b2h-pdp-entry]').forEach(initEntry);
    document.querySelectorAll('[data-b2h-variant-picker]').forEach(initPicker);
    document.querySelectorAll('[data-b2h-size]').forEach(initSize);
    document.querySelectorAll('[data-b2h-details]').forEach(initDetails);
  }
  document.addEventListener('pointerdown', event => {
    for (const disclosure of document.querySelectorAll('[data-pdp-picker-disclosure][open]')) {
      if (!disclosure.closest('[data-b2h-variant-picker]').contains(event.target)) disclosure.open = false;
    }
  });
  mobile.addEventListener('change', () => {
    for (const owner of detailOwners) {
      if (!owner.root.isConnected) detailOwners.delete(owner);
      else owner.refresh();
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',discover,{ once:true });
  else discover();
  document.addEventListener('shopify:section:load',discover);
  document.addEventListener('shopify:section:unload', event => {
    for (const [link,owner] of entryOwners) if (event.target.contains(link)) { owner.controller.abort(); owner.availability.disconnect(); owner.visibility.disconnect(); entryOwners.delete(link); }
    for (const owner of detailOwners) if (event.target.contains(owner.root)) detailOwners.delete(owner);
    for (const dialog of modalOwners) if (event.target.contains(dialog)) { dialog.close(); unlockScroll(dialog); }
  });
})();
